// Slice 7 — Barber leave management.
// Record leave per-barber. Saved rows immediately suppress availability slots
// on the customer-facing booking app (and refuse new customer bookings on
// those dates). Xero remains the source of truth for accruals/balances/STP
// — admins should also enter the same leave there.

import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { PayrollTabs } from '../components/PayrollTabs';

const BRISBANE_TZ = 'Australia/Brisbane';

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--spacing-4)',
  boxShadow: 'var(--shadow-sm)',
};

const inputStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  backgroundColor: 'var(--color-background)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text)',
  width: '100%',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--color-text-muted)',
  marginBottom: 4,
};

const buttonPrimary: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-4)',
  borderRadius: 'var(--radius-default)',
  border: 'none',
  backgroundColor: 'var(--color-accent)',
  color: '#1e1e1e',
  fontWeight: 600,
  fontSize: 'var(--font-size-sm)',
  cursor: 'pointer',
};

const buttonGhost: React.CSSProperties = {
  padding: 'var(--spacing-1) var(--spacing-2)',
  borderRadius: 'var(--radius-default)',
  border: '1px solid var(--color-border)',
  backgroundColor: 'transparent',
  color: 'var(--color-text-muted)',
  fontWeight: 500,
  fontSize: 'var(--font-size-xs)',
  cursor: 'pointer',
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: 'var(--spacing-2) var(--spacing-3)',
  borderBottom: '2px solid var(--color-border)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--color-text-muted)',
};

const tdStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  borderBottom: '1px solid var(--color-border)',
  fontSize: 'var(--font-size-sm)',
};

function formatDate(d: string) {
  const dt = new Date(`${d}T12:00:00`);
  return dt.toLocaleDateString('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: BRISBANE_TZ,
  });
}

function brisbaneToday(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: BRISBANE_TZ });
}

export function PayrollLeave() {
  const queryClient = useQueryClient();

  const [barberId, setBarberId] = useState<string>('');
  const [leaveType, setLeaveType] = useState<string>('Annual');
  const [startDate, setStartDate] = useState<string>(brisbaneToday());
  const [endDate, setEndDate] = useState<string>(brisbaneToday());
  const [description, setDescription] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; label: string } | null>(null);

  const { data: barbersData } = useQuery({
    queryKey: ['barbers'],
    queryFn: () => api.getBarbers(),
  });
  const barbers = useMemo(() => (Array.isArray(barbersData) ? barbersData : []), [barbersData]);

  const { data: typesData } = useQuery({
    queryKey: ['leave-types'],
    queryFn: () => api.payrollLeaveTypes(),
  });
  const leaveTypes = typesData?.leaveTypes ?? [];

  const { data: leaveData, isLoading: leaveLoading } = useQuery({
    queryKey: ['leave-records'],
    queryFn: () => api.payrollListLeave(),
  });
  const leaveRows = leaveData?.leave ?? [];

  async function handleSubmit() {
    if (!barberId) {
      setError('Pick a barber');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.payrollCreateLeave({
        barberId: Number(barberId),
        leaveType,
        startDate,
        endDate,
        description: description.trim() || null,
      });
      queryClient.invalidateQueries({ queryKey: ['leave-records'] });
      // Reset description but keep barber/type for batch entry.
      setDescription('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    const id = deleteTarget.id;
    setDeleteTarget(null);
    try {
      await api.payrollDeleteLeave(id);
      queryClient.invalidateQueries({ queryKey: ['leave-records'] });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    }
  }

  return (
    <div
      style={{
        padding: 'var(--spacing-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--spacing-4)',
        maxWidth: 1200,
      }}
    >
      <div>
        <h1 style={{ margin: 0, marginBottom: 4 }}>Payroll · Leave</h1>
        <div style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
          Record barber leave. Saved leave dates immediately suppress available slots on the
          customer-facing booking app and reject new customer bookings on those days. Xero stays the
          source of truth for accruals and ATO STP categorisation — enter the same leave there too.
        </div>
      </div>

      <PayrollTabs />

      <div style={cardStyle}>
        <h2 style={{ marginTop: 0 }}>Record new leave</h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 'var(--spacing-3)',
            alignItems: 'flex-end',
          }}
        >
          <div>
            <label style={labelStyle}>Barber</label>
            <select
              style={inputStyle}
              value={barberId}
              onChange={(e) => {
                const v = e.currentTarget.value;
                setBarberId(v);
              }}
            >
              <option value="">— Select barber —</option>
              {barbers.map((b: any) => (
                <option key={b.id} value={String(b.id)}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Leave type</label>
            <select
              style={inputStyle}
              value={leaveType}
              onChange={(e) => {
                const v = e.currentTarget.value;
                setLeaveType(v);
              }}
            >
              {leaveTypes.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Start date</label>
            <input
              type="date"
              style={inputStyle}
              value={startDate}
              onChange={(e) => {
                const v = e.currentTarget.value;
                setStartDate(v);
                if (v > endDate) setEndDate(v);
              }}
            />
          </div>
          <div>
            <label style={labelStyle}>End date</label>
            <input
              type="date"
              style={inputStyle}
              value={endDate}
              min={startDate}
              onChange={(e) => {
                const v = e.currentTarget.value;
                setEndDate(v);
              }}
            />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Description (optional)</label>
            <input
              type="text"
              style={inputStyle}
              value={description}
              placeholder="e.g. Holiday in Bali"
              onChange={(e) => {
                const v = e.currentTarget.value;
                setDescription(v);
              }}
            />
          </div>
          <div>
            <button style={buttonPrimary} onClick={handleSubmit} disabled={submitting || !barberId}>
              {submitting ? 'Saving…' : 'Record leave'}
            </button>
          </div>
        </div>
        {error && (
          <div
            style={{
              marginTop: 'var(--spacing-3)',
              padding: 'var(--spacing-2) var(--spacing-3)',
              backgroundColor: 'rgba(229, 62, 62, 0.1)',
              border: '1px solid var(--color-danger, #e53e3e)',
              borderRadius: 'var(--radius-default)',
              color: 'var(--color-danger, #e53e3e)',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            {error}
          </div>
        )}
      </div>

      <div style={cardStyle}>
        <h2 style={{ marginTop: 0 }}>Recorded leave</h2>
        {leaveLoading ? (
          <div>Loading…</div>
        ) : leaveRows.length === 0 ? (
          <div
            style={{
              padding: 'var(--spacing-3)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text-muted)',
              fontStyle: 'italic',
            }}
          >
            No leave recorded yet.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Barber</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>From</th>
                <th style={thStyle}>To</th>
                <th style={thStyle}>Description</th>
                <th style={thStyle}>Recorded by</th>
                <th style={thStyle} />
              </tr>
            </thead>
            <tbody>
              {leaveRows.map((l) => (
                <tr key={l.id}>
                  <td style={tdStyle}>
                    <strong>{l.barberName}</strong>
                  </td>
                  <td style={tdStyle}>{l.leaveType}</td>
                  <td style={tdStyle}>{formatDate(l.startDate)}</td>
                  <td style={tdStyle}>{formatDate(l.endDate)}</td>
                  <td style={tdStyle}>
                    {l.description ?? <span style={{ color: 'var(--color-text-muted)' }}>—</span>}
                  </td>
                  <td
                    style={{
                      ...tdStyle,
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-muted)',
                    }}
                  >
                    {l.createdByEmail ?? '—'}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <button
                      style={buttonGhost}
                      onClick={() =>
                        setDeleteTarget({
                          id: l.id,
                          label: `${l.barberName} · ${l.leaveType} · ${formatDate(l.startDate)} → ${formatDate(l.endDate)}`,
                        })
                      }
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ConfirmDialog
        isOpen={Boolean(deleteTarget)}
        title="Remove this leave record?"
        message={`This will re-enable bookings for ${deleteTarget?.label ?? ''}. The matching record in Xero (if any) is unaffected.`}
        confirmLabel="Remove"
        tone="danger"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
